<template>
  <div class="layout">
    <!-- Top navigation -->
    <header class="topbar">
      <div class="topbar-inner">
        <div class="brand">
          <i class="fa-solid fa-qrcode"></i>
          <span>Evidencia</span>
        </div>

        <nav class="mainnav" aria-label="Primary">
          <router-link
            to="/qr"
            class="navlink"
            :class="{ active: $route.path.startsWith('/qr') }"
          >
            <i class="fa-solid fa-qrcode"></i>
            <span>QR Codes</span>
          </router-link>

          <router-link
            to="/entries"
            class="navlink"
            :class="{ active: $route.path.startsWith('/entries') }"
          >
            <i class="fa-solid fa-user-check"></i>
            <span>Check-In</span>
          </router-link>
        </nav>  
      </div>
    </header>

    <!-- PAGE CONTENT -->
    <div class="page">
      <div class="content">
        <div class="title">
          Evidencia QR
          <div class="toolbar">
            <VueDatePicker
              v-model="dateRange"
              placeholder="QR date range"
              range
              :enable-time-picker="false"
              format="MM/dd/yyyy"
              :max-date="new Date()"
            />
            <div class="search-wrap">
              <i class="fa-solid fa-magnifying-glass"></i>
              <input
                type="text"
                placeholder="Vyhľadávanie… (meno, email, QR…)"
                v-model.trim="search"
              />
            </div>


            <button
              class="btn ghost"
              @click="clearFilters"
              :disabled="!hasAnyFilter"
              title="Vymazať filtre"
            >
              <i class="fa-solid fa-filter-circle-xmark"></i>
              <span class="hide-sm">Vymazať filtre</span>
            </button>

            <button class="btn" @click="openModal({})">
              <i class="fas fa-plus"></i> Nový QR
            </button>
          </div>
        </div>

        <div class="table-card">
          <!-- caption = filter/sort status; header sticks *under* this -->
          <caption class="table-caption" role="status" aria-live="polite">
            <span class="badge">
              <i :class="sortOrder === 'asc' ? 'fa-solid fa-arrow-up' : 'fa-solid fa-arrow-down'"></i>
              Zoradené podľa: <strong>{{ sortKeyLabel }}</strong>
              <em>({{ sortOrder === 'asc' ? 'vzostupne' : 'zostupne' }})</em>
            </span>

            <button class="linklike" @click="setSort('start_from')" title="Reset sort">
              <i class="fa-solid fa-arrow-down-short-wide"></i>
              Reset sort
            </button>
          </caption>

          <table class="qr-table">
            <thead>
              <tr>
                <th @click="setSort('start_from')">
                  Odkedy
                  <SortIcon :active="sortKey==='start_from'" :order="sortOrder"/>
                </th>
                <th @click="setSort('valid_until')">
                  Dokedy
                  <SortIcon :active="sortKey==='valid_until'" :order="sortOrder"/>
                </th>
                <th @click="setSort('surname')">
                  Priezvisko
                  <SortIcon :active="sortKey==='surname'" :order="sortOrder"/>
                </th>
                <th @click="setSort('name')">
                  Meno
                  <SortIcon :active="sortKey==='name'" :order="sortOrder"/>
                </th>
                <th @click="setSort('email')">
                  Email
                  <SortIcon :active="sortKey==='email'" :order="sortOrder"/>
                </th>
                <th @click="setSort('qrcode_value')">
                  qrcode_value
                  <SortIcon :active="sortKey==='qrcode_value'" :order="sortOrder"/>
                </th>
                <th class="th-actions no-sort">Akcie</th>
              </tr>
            </thead>

            <tbody>
              <tr v-for="(item, i) in paginatedData" :key="item.id ?? i">
                <td><span class="muted">{{ formatLocal(item.start_from) }}</span></td>
                <td><span class="muted">{{ formatLocal(item.valid_until) }}</span></td>
                <td class="strong">{{ item.surname }}</td>
                <td>{{ item.name }}</td>
                <td class="mono ellipsis" :title="item.email">{{ item.email }}</td>
                <td class="mono ellipsis" :title="item.qrcode_value">{{ item.qrcode_value }}</td>
                <td class="actions">
                  <button class="icon" @click="openModal(item)" title="Zobraziť" aria-label="Zobraziť">
                    <i class="fas fa-eye"></i>
                  </button>
                  <button class="icon danger" @click="confirmDelete(item.id)" title="Zmazať" aria-label="Zmazať">
                    <i class="fas fa-trash-alt"></i>
                  </button>
                </td>
              </tr>

              <tr v-if="isLoading">
                <td colspan="7" class="empty loading">
                  <i class="fa-solid fa-spinner spin"></i> Načítavam záznamy…
                </td>
              </tr>

              <tr v-if="!isLoading && paginatedData.length === 0">
                <td colspan="7" class="empty">
                  <i class="fa-regular fa-folder-open"></i>
                  Žiadne záznamy. 
                  <button class="linklike" @click="openModal({})">Vytvoriť QR</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="pagination-controls" v-if="totalPages > 1">
          <button class="pager" @click="prevPage" :disabled="currentPage===1" title="Predošlá stránka">
            <i class="fa-solid fa-angle-left"></i>
          </button>

          <span class="pagination-info">
            Stránka <strong>{{ currentPage }}</strong> z {{ totalPages }}
            <span class="dot">•</span>
            Záznamov: <strong>{{ filteredData.length }}</strong>
          </span>

          <button class="pager" @click="nextPage" :disabled="currentPage===totalPages" title="Ďalšia stránka">
            <i class="fa-solid fa-angle-right"></i>
          </button>

          <label class="per-page-wrap">
            <span>na stránku</span>
            <select v-model.number="itemsPerPage" class="per-page">
              <option :value="10">10</option>
              <option :value="20">20</option>
              <option :value="50">50</option>
            </select>
          </label>
        </div>
      </div>

      <!-- Floating Action Button -->
      <button class="fab" @click="openModal({})" title="Nový QR" aria-label="Nový QR">
        <i class="fas fa-plus"></i>
      </button>

      <QrModal ref="qr_modal" @success="reload_data"/>
    </div>
  </div>
</template>

<script>
import VueDatePicker from '@vuepic/vue-datepicker'
import '@vuepic/vue-datepicker/dist/main.css'
import QrModal from './Modals/QrModal.vue'
import ClientService from '@/services/client'
import { toast } from 'vue3-toastify'
import 'vue3-toastify/dist/index.css'

const SortIcon = {
  name: 'SortIcon',
  props: { active: Boolean, order: { type: String, default: 'asc' } },
  template: `
    <span class="sort-icon" :class="{active}">
      <i v-if="!active" class="fa-solid fa-sort"></i>
      <i v-else-if="order==='asc'" class="fa-solid fa-arrow-up-wide-short"></i>
      <i v-else class="fa-solid fa-arrow-down-short-wide"></i>
    </span>`
}

export default {
  name: 'QrList',
  components: { VueDatePicker, QrModal, SortIcon },
  data(){ return {
    isLoading:false, search:'', dateRange:[],
    qrCodes:[], currentPage:1, itemsPerPage:10,
    sortKey:'start_from', sortOrder:'desc',
  }},
  computed:{
    hasAnyFilter(){
      return (this.search?.length>0) || (this.dateRange && this.dateRange.length===2)
    },
    filteredData(){
      const term=this.search.toLowerCase()
      const hasRange=this.dateRange && this.dateRange.length===2
      const [s,e]=hasRange?this.dateRange:[null,null]
      return this.qrCodes.filter(it=>{
        const hay=`${it.name??''} ${it.surname??''} ${it.email??''} ${it.qrcode_value??''}`.toLowerCase()
        const passText=term?hay.includes(term):true
        let passDate=true
        if(hasRange){
          const d=this.normalizeDate(it.start_from)
          passDate=d>=this.normalizeDate(s)&&d<=this.normalizeDate(e)
        }
        return passText && passDate
      })
    },
    sortedData(){
      const arr=[...this.filteredData], k=this.sortKey, ord=this.sortOrder==='asc'?1:-1
      const asD=v=>v?new Date(v).getTime():0, asS=v=>String(v??'').toLowerCase()
      arr.sort((a,b)=>{
        let va=a[k], vb=b[k]
        if(k==='start_from'||k==='valid_until'){ va=asD(va); vb=asD(vb) } else { va=asS(va); vb=asS(vb) }
        return va<vb?-1*ord:va>vb?1*ord:0
      })
      return arr
    },
    totalPages(){ return Math.max(1, Math.ceil(this.sortedData.length/this.itemsPerPage)) },
    paginatedData(){
      const start=(this.currentPage-1)*this.itemsPerPage
      return this.sortedData.slice(start,start+this.itemsPerPage)
    },
    sortKeyLabel(){
      const map={
        start_from:'Odkedy',
        valid_until:'Dokedy',
        surname:'Priezvisko',
        name:'Meno',
        email:'Email',
        qrcode_value:'qrcode_value'
      }
      return map[this.sortKey] ?? this.sortKey
    },
  },
  methods:{
    async reload_data(){
      try{
        this.isLoading=true
        const r=await ClientService.getQrCodes()
        this.qrCodes=Array.isArray(r.data)?r.data:[]
        this.currentPage=1
      }catch{
        localStorage.setItem('token','')
        this.$router.push('/')
      }finally{
        this.isLoading=false
      }
    },
    confirmDelete(id){
      if(!id) return
      const ok = confirm('Naozaj chcete zmazať tento QR?')
      if(ok) this.deletePayment(id)
    },
    deletePayment(id){
      ClientService.deleteQrCode(id)
        .then(()=>{
          toast('QR úspešne zmazaný',{autoClose:3000})
          this.reload_data()
        })
        .catch(()=>{
          localStorage.setItem('token','')
          this.$router.push('/')
        })
    },
    openModal(item){ this.$refs.qr_modal.openModal(item) },
    clearFilters(){ this.search=''; this.dateRange=[]; this.currentPage=1 },
    nextPage(){ if(this.currentPage<this.totalPages) this.currentPage++ },
    prevPage(){ if(this.currentPage>1) this.currentPage-- },
    setSort(key){
      if(this.sortKey===key){
        this.sortOrder=this.sortOrder==='asc'?'desc':'asc'
      }else{
        this.sortKey=key
        this.sortOrder='asc'
      }
    },
    normalizeDate(d){
      if(!d) return new Date(0)
      const x=new Date(d)
      return new Date(x.getFullYear(),x.getMonth(),x.getDate())
    },
    formatLocal(d){
      if(!d) return ''
      const x=new Date(d)
      return `${x.getFullYear()}-${String(x.getMonth()+1).padStart(2,'0')}-${String(x.getDate()).padStart(2,'0')}`
    },
    _qrHotkey(e){
      const isMac = navigator.platform.toUpperCase().includes('MAC')
      if ((isMac ? e.metaKey : e.ctrlKey) && e.key.toLowerCase() === 'n'){
        e.preventDefault()
        this.openModal({})
      }
    }
  },
  watch:{ search(){this.currentPage=1}, dateRange(){this.currentPage=1}, itemsPerPage(){this.currentPage=1} },
  mounted(){ 
    this.reload_data()
    window.addEventListener('keydown', this._qrHotkey)
  },
  beforeUnmount(){
    window.removeEventListener('keydown', this._qrHotkey)
  }
}
</script>

<style scoped>
/* Layout + topbar */
.layout {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background:
    linear-gradient(rgba(0,0,0,0.45), rgba(0,0,0,0.45)),
    url('@/assets/bg.jpg') center/cover no-repeat;
}

.topbar{
  position:sticky; top:0; z-index:20;
  backdrop-filter:saturate(140%) blur(10px);
  background:#79baf4;
  border-bottom:1px solid rgba(0,0,0,.06);
}
.topbar-inner{
  max-width:1200px; margin:0 auto;
  display:flex; align-items:center; gap:12px;
  padding:10px 16px;
}
.brand{ display:flex; align-items:center; gap:10px; font-weight:800; color:#0a2a47; }
.brand i{ color:#369af1; }
.mainnav{ display:flex; gap:6px; align-items:center; margin-left:8px; }
.navlink{
  display:inline-flex; align-items:center; gap:8px;
  padding:8px 12px; border-radius:10px; text-decoration:none;
  color:#0f172a; border:1px solid transparent;
  transition:background .15s ease, border-color .15s ease, color .15s ease;
  font-weight:600;
}
.navlink:hover{ background:#369af1; color:#fff; border-color:#369af1;}
.navlink.active{ background:#369af1; color:#fff; border-color:#369af1; }
.topbar-actions{ margin-left:auto; display:flex; align-items:center; }
.hide-sm{ display:inline; }
@media (max-width: 640px){
  .hide-sm{ display:none; }
}

/* Page container */
.page{ padding:24px; width:1100px; margin:0 auto; }

/* card */
.content{
  border-radius:18px; padding:16px;
  box-shadow:0 12px 40px rgba(0,0,0,.18);
  background: rgba(255,255,255,.92);
  backdrop-filter: blur(6px);
}
.title{ font-size:22px; font-weight:800; margin:6px 0 14px; display:flex; align-items:center; justify-content:space-between; }

/* toolbar */
.toolbar{
  display:flex; gap:10px; flex-wrap:wrap; align-items:center;
  background:#fff; padding:12px 16px; border-radius:12px;
  border:1px solid #e6ebf2;
  box-shadow:0 2px 6px rgba(0,0,0,0.06);
}
.search-wrap{ flex:1 1 260px; min-width:220px; position:relative; }
.search-wrap i{
  position:absolute; left:10px; top:50%; transform:translateY(-50%); opacity:.6; font-size:14px;
}
.search-wrap input{
  width:90%; padding:8px 10px 8px 30px;
  border:1px solid #e6ebf2; border-radius:10px;
}

.btn{
  display:inline-flex; align-items:center; gap:8px;
  padding:8px 12px; border-radius:10px;
  border:1px solid #369af1; background:#369af1; color:#fff; font-weight:600;
  transition: filter .15s ease, box-shadow .15s ease;
}
.btn:hover{ filter:brightness(.95); box-shadow:0 4px 12px rgba(54,154,241,.28); }
.btn.ghost{ background:#fff; color:#369af1; }
.btn.ghost:disabled{ opacity:.5; cursor:not-allowed; }
.topbar .btn.createqr{ background:#369af1; border-color:#369af1; color:#ffffff; }

/* table container */
.table-card{
  margin-top:14px; background:#fff; border-radius:12px;
  border:1px solid #e6ebf2;
  box-shadow:0 10px 26px rgba(0,0,0,.10); overflow:hidden;
  position:relative;
}

/* caption */
caption.table-caption{
  --caption-h: 44px; /* layout offset, not a color */
  display:flex; align-items:center; justify-content:space-between; gap:8px;
  min-height: var(--caption-h);
  padding:8px 12px; background:#f8fbff; border-bottom:1px solid #e6ebf2;
}
caption .badge{
  display:inline-flex; align-items:center; gap:8px;
  padding:6px 10px; border-radius:10px; 
  color:#0a2a47; font-weight:600;
}
caption .badge em{ font-style:normal; color:#64748b; margin-left:6px; }
.linklike{
  border:none; background:none; color:#369af1; font-weight:700; cursor:pointer;
  text-decoration:underline;
}

/* table */
.qr-table{
  width:100%;
  table-layout:auto;            /* automatic column widths */
  border-collapse: collapse;
  font-size:14px;
}
thead th{
  position:sticky; top: var(--caption-h);
  background:#eef6ff;
  color:#0b2540; border-bottom:1px solid #e6ebf2;
  padding:12px; text-align:left; font-weight:800; letter-spacing:.2px; z-index:1;
  cursor:pointer;
}
thead th.no-sort{ cursor:default; }
tbody td{
  padding:10px 12px; text-align:left; border-bottom:1px solid #e6ebf2; background:#ffffff;
}
tbody tr:nth-child(even) td{ background:#f9fbfe; }
tbody tr:hover td{ background:#f1f7ff; }

/* cells & actions */
.mono{ font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono','Courier New', monospace; }
.strong{ font-weight:700; }
.muted{ color:#64748b; }
.ellipsis{ overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.actions{ text-align:center; white-space:nowrap; }
.th-actions{ text-align:center; width:110px; }
.icon{
  border:1px solid #e6ebf2; background:#fff; padding:6px 8px; border-radius:8px; margin-right:6px; cursor:pointer;
  transition: background .12s ease, transform .06s ease;
}
.icon:last-child{ margin-right:0; }
.icon:hover{ background:#f3f6fb; transform: translateY(-1px); }
.icon.danger{ color:#ef4444; border-color:#f3d1d1; }
.icon.danger:hover{ background:#fde8e8; }

/* sort + pagination */
.sort-icon{ margin-left:6px; opacity:.6; }
.sort-icon.active{ opacity:1; }
.pagination-controls{
  display:flex; gap:10px; align-items:center; justify-content:center; padding:14px 0; flex-wrap:wrap;
}
.pager{ border:1px solid #d0d7de; background:#fff; padding:6px 10px; border-radius:8px; }
.pager:disabled{ opacity:.5; cursor:not-allowed; }
.pagination-info{ color:#64748b; }
.pagination-info strong{ color:#0f172a; }
.dot{ margin:0 6px; opacity:.5; }
.per-page-wrap{ display:inline-flex; align-items:center; gap:6px; margin-left:6px; color:#64748b; }
.per-page{ padding:6px 8px; border-radius:8px; border:1px solid #e6ebf2; background:#fff; }

/* empty / loading */
.empty{ text-align:center; padding:28px 0; color:#64748b; }
.empty i{ margin-right:8px; }
.loading .spin{ animation:spin 1s linear infinite; }
@keyframes spin{ to{ transform:rotate(360deg); } }

/* Floating action button */
.fab{
  position: fixed; right: 22px; bottom: 22px; z-index: 50;
  width: 56px; height: 56px; border-radius: 999px;
  display: inline-flex; align-items: center; justify-content: center;
  background:#369af1; color:#fff; border: none;
  box-shadow: 0 10px 24px rgba(0,0,0,.18);
  cursor: pointer;
}
.fab:hover{ filter: brightness(.95); }
.fab i{ font-size: 18px; }
</style>
